import { track } from '@trello/analytics';
import { forTemplate } from 'app/common/lib/util/i18n';
import React from 'react';
import { arrayRange } from 'app/gamma/src/util/array-range';
import styles from './UpgradePromptBanner.less';

const format = forTemplate('paid_product_info');

export enum UpgradePromptBannerType {
  BusinessClass = 'business-class',
  BusinessClassUpgrade = 'business-class-upgrade',
  Gold = 'gold',
}

interface UpgradePromptBannerProps {
  type: UpgradePromptBannerType;
  infoUrl: string;
  subtitleCount?: number;
  epilogueCount?: number;
}

const featureImagesMapping = {
  'business-class': [
    require('resources/images/business-class/feature-boards.svg'),
    require('resources/images/business-class/feature-powerups.svg'),
    require('resources/images/business-class/feature-butler.svg'),
    require('resources/images/business-class/feature-control.svg'),
  ],
  'business-class-upgrade': [
    require('resources/images/business-class-feature-boards.png'),
    require('resources/images/business-class-feature-integration.png'),
    require('resources/images/business-class-feature-roadmap.png'),
  ],
  gold: [
    require('resources/images/business-class-feature-boards.png'),
    require('resources/images/business-class-feature-integration.png'),
    require('resources/images/business-class-feature-roadmap.png'),
  ],
};

export const UpgradePromptBanner: React.FC<UpgradePromptBannerProps> = ({
  type,
  infoUrl,
  subtitleCount = 0,
  epilogueCount = 0,
}) => {
  const features = type === 'business-class' ? 4 : 3;
  const trackBCLink = () =>
    track('Billing Page', 'See All Benefits Link Click');

  if (type !== 'business-class-upgrade' && epilogueCount > 1) {
    throw new Error(
      'Only use epilogueCount with the type business-class-upgrade',
    );
  }

  return (
    <div className={styles.upgradePromptBanner}>
      <header>
        <h2>{format([type, 'title'])}</h2>
        {subtitleCount > 0 &&
          arrayRange(0, subtitleCount - 1).map((i) => (
            <p key={`subtitles-${i}`}>
              {format([type, 'subtitles', i.toString()])}
            </p>
          ))}
      </header>

      <div className={styles.features}>
        {arrayRange(0, features - 1).map((i) => (
          <div className={styles.feature} key={`features-${i}`}>
            <img
              src={featureImagesMapping[type][i]}
              alt={format([type, 'featureHeaders', i.toString()])}
            />
            <h3>{format([type, 'featureHeaders', i.toString()])}</h3>
            <p>{format([type, 'featureBodies', i.toString()])}</p>
          </div>
        ))}
      </div>

      {epilogueCount > 0 && (
        <div className={styles.epilogue}>
          {arrayRange(0, epilogueCount - 1).map((i) => (
            <p key={`epilogue-${i}`}>
              {format(
                [type, 'epilogue recent', i.toString()],
                i === 1
                  ? {
                      smartBilling: (
                        <strong key="smartBilling">
                          {format('smartBilling')}
                        </strong>
                      ),
                    }
                  : {},
              )}
            </p>
          ))}
        </div>
      )}

      <div>
        <a href={infoUrl} target="_blank" onClick={trackBCLink}>
          {format([type, 'link'])}
        </a>
      </div>
    </div>
  );
};
